// Lock icon variants for password tiles. Each renders inside a small
// (viewBox 0 0 64 64) SVG so it can sit in the bottom-right of a tile via
// .treasure-lock-icon CSS class. A diagonal sheen is rendered inside each
// SVG and clipped to the icon's silhouette via an alpha mask, so the
// reflection only sweeps over the icon shape — not over the transparent
// area around it.

import { useId } from "react";

const LockSvg = ({ children }) => {
  const rawId = useId().replace(/:/g, "");
  const maskId = `lockMask-${rawId}`;
  const gradId = `lockShine-${rawId}`;
  return (
    <span className="treasure-lock-icon">
      <svg viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <mask
            id={maskId}
            maskType="alpha"
            maskUnits="userSpaceOnUse"
            x="0"
            y="0"
            width="64"
            height="64"
          >
            {children}
          </mask>
          <linearGradient id={gradId} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="white" stopOpacity="0" />
            <stop offset="42%" stopColor="white" stopOpacity="0" />
            <stop offset="50%" stopColor="white" stopOpacity="0.85" />
            <stop offset="58%" stopColor="white" stopOpacity="0" />
            <stop offset="100%" stopColor="white" stopOpacity="0" />
            <animateTransform
              attributeName="gradientTransform"
              type="translate"
              values="-1 -1; -1 -1; 1 1; 1 1"
              keyTimes="0; 0.25; 0.7; 1"
              dur="2s"
              repeatCount="indefinite"
            />
          </linearGradient>
        </defs>
        {children}
        <rect
          width="64"
          height="64"
          fill={`url(#${gradId})`}
          mask={`url(#${maskId})`}
        />
      </svg>
    </span>
  );
};

const wrap = (children) => <LockSvg>{children}</LockSvg>;

// 1. Schatkist (huidige variant)
export const TreasureChestIcon = () =>
  wrap(
    <>
      <rect x="8" y="28" width="48" height="30" rx="4" fill="#8B5E3C" stroke="#5C3A1E" strokeWidth="2" />
      <rect x="8" y="28" width="48" height="12" rx="4" fill="#A0724A" />
      <path d="M8 32 Q32 22 56 32" fill="#6B4226" stroke="#5C3A1E" strokeWidth="1.5" />
      <rect x="26" y="32" width="12" height="10" rx="2" fill="#FFD700" stroke="#B8860B" strokeWidth="1.5" />
      <circle cx="32" cy="38" r="2" fill="#5C3A1E" />
      <rect x="12" y="30" width="4" height="24" rx="1" fill="#6B4226" />
      <rect x="48" y="30" width="4" height="24" rx="1" fill="#6B4226" />
    </>
  );

// 2. Klassiek hangslot (goud)
export const ClassicPadlockIcon = () =>
  wrap(
    <>
      <path d="M20 30 V22 a12 12 0 0 1 24 0 V30" fill="none" stroke="#5C3A1E" strokeWidth="5" strokeLinecap="round" />
      <rect x="14" y="28" width="36" height="28" rx="4" fill="#FFD700" stroke="#B8860B" strokeWidth="2" />
      <rect x="14" y="28" width="36" height="6" rx="3" fill="#FFE85C" opacity="0.7" />
      <circle cx="32" cy="40" r="4" fill="#5C3A1E" />
      <rect x="30.5" y="42" width="3" height="8" fill="#5C3A1E" />
    </>
  );

// 3. Piraten-schedel
export const SkullLockIcon = () =>
  wrap(
    <>
      <path d="M16 28 a16 16 0 0 1 32 0 v10 a4 4 0 0 1 -4 4 h-3 v6 h-18 v-6 h-3 a4 4 0 0 1 -4 -4 z" fill="#F1ECE2" stroke="#2A2118" strokeWidth="2" />
      <circle cx="24" cy="32" r="4" fill="#2A2118" />
      <circle cx="40" cy="32" r="4" fill="#2A2118" />
      <path d="M30 40 l2 -3 l2 3 l-2 3 z" fill="#2A2118" />
      <rect x="26" y="44" width="2" height="4" fill="#2A2118" />
      <rect x="30" y="44" width="2" height="4" fill="#2A2118" />
      <rect x="34" y="44" width="2" height="4" fill="#2A2118" />
      <rect x="38" y="44" width="2" height="4" fill="#2A2118" />
      <path d="M14 52 l36 -8" stroke="#5C4A2A" strokeWidth="3" strokeLinecap="round" />
      <path d="M50 52 l-36 -8" stroke="#5C4A2A" strokeWidth="3" strokeLinecap="round" />
    </>
  );

// 4. Antiek sleutelgat
export const OrnateKeyholeIcon = () =>
  wrap(
    <>
      <rect x="10" y="8" width="44" height="48" rx="6" fill="#2A2118" stroke="#FFD700" strokeWidth="2.5" />
      <rect x="14" y="12" width="36" height="40" rx="4" fill="none" stroke="#FFD700" strokeWidth="1" opacity="0.6" />
      <circle cx="32" cy="26" r="6" fill="#FFD700" />
      <circle cx="32" cy="26" r="3" fill="#2A2118" />
      <path d="M29 30 L26 48 L38 48 L35 30 Z" fill="#FFD700" />
      <path d="M28 38 L36 38" stroke="#2A2118" strokeWidth="1.5" />
    </>
  );

// 5. Edelsteen / diamant
export const GemLockIcon = () =>
  wrap(
    <>
      <path d="M16 24 L32 8 L48 24 L32 58 Z" fill="#5BD0E8" stroke="#1F7A8C" strokeWidth="2" />
      <path d="M16 24 L32 8 L32 24 Z" fill="#9DEBF7" />
      <path d="M48 24 L32 8 L32 24 Z" fill="#3DA9C2" />
      <path d="M16 24 L32 24 L32 58 Z" fill="#7BDDED" />
      <path d="M48 24 L32 24 L32 58 Z" fill="#2D90A8" />
      <circle cx="22" cy="14" r="1.5" fill="white" opacity="0.9" />
      <circle cx="40" cy="40" r="1" fill="white" opacity="0.6" />
    </>
  );

// 6. Perkament met lakzegel
export const ScrollSealIcon = () =>
  wrap(
    <>
      <rect x="10" y="14" width="44" height="36" rx="2" fill="#F4E4BC" stroke="#8B6F3C" strokeWidth="1.5" />
      <path d="M10 14 q-4 0 -4 4 t4 4 v26 q-4 0 -4 4 t4 4" fill="#E8D29A" stroke="#8B6F3C" strokeWidth="1.5" />
      <path d="M54 14 q4 0 4 4 t-4 4 v26 q4 0 4 4 t-4 4" fill="#E8D29A" stroke="#8B6F3C" strokeWidth="1.5" />
      <line x1="18" y1="24" x2="46" y2="24" stroke="#8B6F3C" strokeWidth="1" />
      <line x1="18" y1="30" x2="44" y2="30" stroke="#8B6F3C" strokeWidth="1" />
      <line x1="18" y1="36" x2="46" y2="36" stroke="#8B6F3C" strokeWidth="1" />
      <circle cx="32" cy="44" r="7" fill="#C0392B" stroke="#7B1F12" strokeWidth="1.5" />
      <path d="M28 41 L32 47 L36 41 M30 44 L34 44" stroke="#7B1F12" strokeWidth="1.2" fill="none" strokeLinecap="round" />
    </>
  );

// 7. Kluis-wieltje
export const VaultDialIcon = () =>
  wrap(
    <>
      <circle cx="32" cy="32" r="26" fill="#3A3A3A" stroke="#1F1F1F" strokeWidth="2" />
      <circle cx="32" cy="32" r="20" fill="#5A5A5A" stroke="#1F1F1F" strokeWidth="1.5" />
      <circle cx="32" cy="32" r="14" fill="#7A7A7A" />
      <circle cx="32" cy="32" r="3" fill="#FFD700" />
      <line x1="32" y1="6" x2="32" y2="12" stroke="#FFD700" strokeWidth="2" strokeLinecap="round" />
      <line x1="32" y1="52" x2="32" y2="58" stroke="#FFD700" strokeWidth="1.5" strokeLinecap="round" />
      <line x1="6" y1="32" x2="12" y2="32" stroke="#FFD700" strokeWidth="1.5" strokeLinecap="round" />
      <line x1="52" y1="32" x2="58" y2="32" stroke="#FFD700" strokeWidth="1.5" strokeLinecap="round" />
      <line x1="32" y1="32" x2="44" y2="20" stroke="#FFD700" strokeWidth="2.5" strokeLinecap="round" />
    </>
  );

// 8. Vraagteken-mysterie
export const MysteryIcon = () =>
  wrap(
    <>
      <path d="M32 4 L56 14 V34 q0 18 -24 26 q-24 -8 -24 -26 V14 Z" fill="#5B2A86" stroke="#FFD700" strokeWidth="2.5" />
      <path d="M24 24 a8 8 0 0 1 16 0 c0 4 -4 6 -6 8 c-1 1 -2 3 -2 5" fill="none" stroke="#FFD700" strokeWidth="3.5" strokeLinecap="round" />
      <circle cx="32" cy="44" r="2.5" fill="#FFD700" />
    </>
  );

// 9. Slot met kroon
export const CrownLockIcon = () =>
  wrap(
    <>
      <path d="M14 20 L20 8 L26 16 L32 6 L38 16 L44 8 L50 20 Z" fill="#FFD700" stroke="#B8860B" strokeWidth="1.5" strokeLinejoin="round" />
      <circle cx="20" cy="8" r="2" fill="#E74C3C" />
      <circle cx="32" cy="6" r="2" fill="#27AE60" />
      <circle cx="44" cy="8" r="2" fill="#3498DB" />
      <rect x="14" y="20" width="36" height="4" fill="#B8860B" />
      <rect x="16" y="28" width="32" height="28" rx="3" fill="#FFD700" stroke="#B8860B" strokeWidth="2" />
      <circle cx="32" cy="40" r="4" fill="#5C3A1E" />
      <rect x="30.5" y="42" width="3" height="8" fill="#5C3A1E" />
    </>
  );

// 10. Kompasroos (piraten-thema)
export const CompassRoseIcon = () =>
  wrap(
    <>
      <circle cx="32" cy="32" r="26" fill="#F4E4BC" stroke="#5C3A1E" strokeWidth="2.5" />
      <circle cx="32" cy="32" r="22" fill="none" stroke="#5C3A1E" strokeWidth="0.8" />
      <path d="M32 8 L36 32 L32 56 L28 32 Z" fill="#C0392B" stroke="#5C3A1E" strokeWidth="1" />
      <path d="M8 32 L32 28 L56 32 L32 36 Z" fill="#FFD700" stroke="#5C3A1E" strokeWidth="1" />
      <path d="M32 8 L32 32 L28 32 Z" fill="#E74C3C" />
      <path d="M8 32 L32 32 L32 28 Z" fill="#FFE85C" />
      <circle cx="32" cy="32" r="2.5" fill="#5C3A1E" />
      <text x="32" y="14" textAnchor="middle" fontSize="6" fill="#5C3A1E" fontWeight="bold">N</text>
    </>
  );

// 11. Hartslot
export const HeartLockIcon = () =>
  wrap(
    <>
      <path
        d="M32 56 C 8 40 8 18 22 18 a 10 10 0 0 1 10 8 a 10 10 0 0 1 10 -8 c 14 0 14 22 -10 38 z"
        fill="#E74C3C"
        stroke="#7B1F12"
        strokeWidth="2"
      />
      <circle cx="32" cy="34" r="3.5" fill="#2A0F0D" />
      <rect x="30.5" y="36" width="3" height="8" fill="#2A0F0D" />
    </>
  );

// 12. Boekslot (oud boek met sluiting)
export const BookLockIcon = () =>
  wrap(
    <>
      <rect x="10" y="10" width="44" height="48" rx="2" fill="#5C3A1E" stroke="#2A1810" strokeWidth="2" />
      <rect x="14" y="14" width="36" height="40" fill="#8B5E3C" />
      <line x1="14" y1="22" x2="50" y2="22" stroke="#FFD700" strokeWidth="1" />
      <line x1="14" y1="46" x2="50" y2="46" stroke="#FFD700" strokeWidth="1" />
      <rect x="26" y="6" width="12" height="22" rx="1" fill="#FFD700" stroke="#B8860B" strokeWidth="1.5" />
      <rect x="26" y="32" width="12" height="22" rx="1" fill="#FFD700" stroke="#B8860B" strokeWidth="1.5" />
      <circle cx="32" cy="34" r="2.5" fill="#5C3A1E" />
    </>
  );

// 13. Drakenoog
export const DragonEyeIcon = () =>
  wrap(
    <>
      <ellipse cx="32" cy="32" rx="26" ry="18" fill="#FFD700" stroke="#5C3A1E" strokeWidth="2.5" />
      <ellipse cx="32" cy="32" rx="20" ry="14" fill="#E74C3C" />
      <ellipse cx="32" cy="32" rx="14" ry="10" fill="#FFE85C" />
      <ellipse cx="32" cy="32" rx="3" ry="9" fill="#2A0F0D" />
      <circle cx="29" cy="28" r="1.5" fill="white" opacity="0.85" />
      <path d="M6 32 Q12 26 18 32 Q12 38 6 32" fill="#5C3A1E" opacity="0.6" />
      <path d="M58 32 Q52 26 46 32 Q52 38 58 32" fill="#5C3A1E" opacity="0.6" />
    </>
  );

// 14. Kristallen Bol
export const CrystalBallIcon = () =>
  wrap(
    <>
      <ellipse cx="32" cy="56" rx="14" ry="3" fill="#3A2618" />
      <path d="M22 50 L18 58 L46 58 L42 50 Z" fill="#8B5E3C" stroke="#5C3A1E" strokeWidth="1.5" />
      <circle cx="32" cy="30" r="20" fill="#A78EE6" stroke="#5B2A86" strokeWidth="2" />
      <ellipse cx="26" cy="22" rx="6" ry="4" fill="white" opacity="0.5" />
      <circle cx="38" cy="38" r="2" fill="white" opacity="0.4" />
      <circle cx="22" cy="36" r="1" fill="white" opacity="0.3" />
    </>
  );

// 15. Anker
export const AnchorIcon = () =>
  wrap(
    <>
      <circle cx="32" cy="14" r="6" fill="none" stroke="#5C3A1E" strokeWidth="3.5" />
      <line x1="32" y1="20" x2="32" y2="56" stroke="#5C3A1E" strokeWidth="4" strokeLinecap="round" />
      <line x1="22" y1="26" x2="42" y2="26" stroke="#5C3A1E" strokeWidth="3.5" strokeLinecap="round" />
      <path d="M10 40 Q10 56 32 56 Q54 56 54 40" fill="none" stroke="#5C3A1E" strokeWidth="4" strokeLinecap="round" />
      <line x1="10" y1="40" x2="6" y2="36" stroke="#5C3A1E" strokeWidth="3" strokeLinecap="round" />
      <line x1="54" y1="40" x2="58" y2="36" stroke="#5C3A1E" strokeWidth="3" strokeLinecap="round" />
    </>
  );

// 16. Tandwiel
export const GearIcon = () => {
  const teeth = [0, 45, 90, 135, 180, 225, 270, 315];
  return wrap(
    <>
      <g transform="translate(32 32)">
        {teeth.map((angle) => (
          <rect
            key={angle}
            x="-4"
            y="-30"
            width="8"
            height="10"
            fill="#7A7A7A"
            stroke="#3A3A3A"
            strokeWidth="1"
            transform={`rotate(${angle})`}
          />
        ))}
      </g>
      <circle cx="32" cy="32" r="20" fill="#9A9A9A" stroke="#3A3A3A" strokeWidth="2" />
      <circle cx="32" cy="32" r="8" fill="#3A3A3A" />
      <circle cx="32" cy="32" r="3" fill="#7A7A7A" />
    </>
  );
};

// 17. Lantaarn
export const LanternIcon = () =>
  wrap(
    <>
      <path d="M30 4 a2 2 0 0 1 4 0 v6 h-4 z" fill="#5C3A1E" />
      <rect x="20" y="10" width="24" height="4" rx="1" fill="#5C3A1E" />
      <rect x="22" y="14" width="20" height="36" rx="2" fill="#FFD700" stroke="#5C3A1E" strokeWidth="2" />
      <rect x="24" y="16" width="16" height="32" fill="#FFE85C" opacity="0.85" />
      <line x1="22" y1="22" x2="42" y2="22" stroke="#5C3A1E" strokeWidth="1" />
      <line x1="22" y1="42" x2="42" y2="42" stroke="#5C3A1E" strokeWidth="1" />
      <ellipse cx="32" cy="32" rx="5" ry="7" fill="#FFA500" />
      <ellipse cx="32" cy="32" rx="2.5" ry="4" fill="#FFFF99" />
      <rect x="20" y="50" width="24" height="6" rx="1" fill="#5C3A1E" />
    </>
  );

// 18. Doodshoofd-munt
export const SkullCoinIcon = () =>
  wrap(
    <>
      <circle cx="32" cy="32" r="26" fill="#FFD700" stroke="#B8860B" strokeWidth="2.5" />
      <circle cx="32" cy="32" r="22" fill="none" stroke="#B8860B" strokeWidth="0.8" strokeDasharray="2 2" />
      <path
        d="M22 28 a10 10 0 0 1 20 0 v6 a3 3 0 0 1 -3 3 h-2 v4 h-10 v-4 h-2 a3 3 0 0 1 -3 -3 z"
        fill="#5C3A1E"
      />
      <circle cx="28" cy="30" r="2.5" fill="#FFD700" />
      <circle cx="36" cy="30" r="2.5" fill="#FFD700" />
      <rect x="28" y="38" width="1.5" height="3" fill="#5C3A1E" />
      <rect x="31" y="38" width="1.5" height="3" fill="#5C3A1E" />
      <rect x="34" y="38" width="1.5" height="3" fill="#5C3A1E" />
      <line x1="20" y1="48" x2="44" y2="48" stroke="#5C3A1E" strokeWidth="2" strokeLinecap="round" />
    </>
  );

// 19. Alziend Oog
export const AllSeeingEyeIcon = () =>
  wrap(
    <>
      <path d="M32 6 L58 52 L6 52 Z" fill="#FFD700" stroke="#5C3A1E" strokeWidth="2.5" strokeLinejoin="round" />
      <path d="M14 38 Q32 24 50 38 Q32 50 14 38 Z" fill="white" stroke="#5C3A1E" strokeWidth="1.5" />
      <circle cx="32" cy="38" r="6" fill="#3DA9C2" />
      <circle cx="32" cy="38" r="3" fill="#2A0F0D" />
      <circle cx="33" cy="36" r="1" fill="white" />
    </>
  );

// 20. Zandloper
export const HourglassIcon = () =>
  wrap(
    <>
      <rect x="14" y="6" width="36" height="4" fill="#5C3A1E" />
      <rect x="14" y="54" width="36" height="4" fill="#5C3A1E" />
      <path
        d="M16 10 L48 10 L36 32 L48 54 L16 54 L28 32 Z"
        fill="#E8D29A"
        fillOpacity="0.5"
        stroke="#5C3A1E"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <path d="M18 12 L46 12 L34 30 L30 30 Z" fill="#FFD700" opacity="0.85" />
      <circle cx="32" cy="34" r="0.8" fill="#FFD700" />
      <circle cx="33" cy="36" r="0.8" fill="#FFD700" />
      <circle cx="31" cy="38" r="0.8" fill="#FFD700" />
      <path d="M22 52 L42 52 L38 44 L26 44 Z" fill="#FFD700" opacity="0.7" />
    </>
  );

export const lockOptions = [
  { name: "Schatkist", id: "TreasureChest", Component: TreasureChestIcon },
  { name: "Klassiek Hangslot", id: "ClassicPadlock", Component: ClassicPadlockIcon },
  { name: "Piraten-schedel", id: "SkullLock", Component: SkullLockIcon },
  { name: "Antiek Sleutelgat", id: "OrnateKeyhole", Component: OrnateKeyholeIcon },
  { name: "Edelsteen", id: "GemLock", Component: GemLockIcon },
  { name: "Perkament met Zegel", id: "ScrollSeal", Component: ScrollSealIcon },
  { name: "Kluiswiel", id: "VaultDial", Component: VaultDialIcon },
  { name: "Mysterie Schild", id: "Mystery", Component: MysteryIcon },
  { name: "Slot met Kroon", id: "CrownLock", Component: CrownLockIcon },
  { name: "Kompasroos", id: "CompassRose", Component: CompassRoseIcon },
  { name: "Hartslot", id: "HeartLock", Component: HeartLockIcon },
  { name: "Boekslot", id: "BookLock", Component: BookLockIcon },
  { name: "Drakenoog", id: "DragonEye", Component: DragonEyeIcon },
  { name: "Kristallen Bol", id: "CrystalBall", Component: CrystalBallIcon },
  { name: "Anker", id: "Anchor", Component: AnchorIcon },
  { name: "Tandwiel", id: "Gear", Component: GearIcon },
  { name: "Lantaarn", id: "Lantern", Component: LanternIcon },
  { name: "Doodshoofd-munt", id: "SkullCoin", Component: SkullCoinIcon },
  { name: "Alziend Oog", id: "AllSeeingEye", Component: AllSeeingEyeIcon },
  { name: "Zandloper", id: "Hourglass", Component: HourglassIcon },
];
